export type FullscreenButtonPosition = "top-left" | "top-right" | "bottom-left" | "bottom-right";

export interface FullscreenButtonOptions {
  /** Coin de l'écran où placer le bouton. Par défaut : "bottom-right", juste au-dessus de `ZoomControl`. */
  position?: FullscreenButtonPosition;
  /** Fusionné par-dessus le style par défaut — même convention que `ResetViewButtonOptions.style`. */
  style?: Partial<CSSStyleDeclaration>;
}

const POSITION_OFFSETS: Record<FullscreenButtonPosition, Partial<CSSStyleDeclaration>> = {
  "top-left": { top: "10px", left: "10px" },
  "top-right": { top: "10px", right: "10px" },
  "bottom-left": { bottom: "10px", left: "10px" },
  "bottom-right": { bottom: "10px", right: "10px" },
};

// Même apparence que `ResetViewButton`/`GeolocationButton`, avec qui il s'empile.
const DEFAULT_STYLE: Partial<CSSStyleDeclaration> = {
  position: "absolute",
  zIndex: "10",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "36px",
  height: "36px",
  padding: "0",
  background: "#fff",
  color: "#444",
  border: "1px solid #ddd",
  borderRadius: "8px",
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.2)",
  cursor: "pointer",
};

const ENTER_LABEL = "Plein écran";
const EXIT_LABEL = "Quitter le plein écran";

/** Affiche `target` en plein écran, ou l'en sort. */
export class FullscreenButton {
  private el: HTMLButtonElement;
  private edgeProperty: "top" | "bottom";
  private readonly onFullscreenChange = () => this.render();

  /** Le navigateur peut refuser le plein écran à un élément (iPhone, cadre sans `allowfullscreen`) : le bouton n'aurait alors aucun effet. */
  static isSupported(): boolean {
    return typeof document !== "undefined" && document.fullscreenEnabled;
  }

  constructor(container: HTMLElement, private readonly target: HTMLElement, options: FullscreenButtonOptions) {
    this.el = document.createElement("button");
    this.el.type = "button";
    this.el.className = "toka-fullscreen-button";
    Object.assign(this.el.style, DEFAULT_STYLE, POSITION_OFFSETS[options.position ?? "bottom-right"], options.style);
    this.edgeProperty = (options.position ?? "bottom-right").startsWith("top") ? "top" : "bottom";
    this.el.addEventListener("click", () => this.toggle());
    document.addEventListener("fullscreenchange", this.onFullscreenChange);
    container.appendChild(this.el);
    this.render();
  }

  private get isFullscreen(): boolean {
    return document.fullscreenElement === this.target;
  }

  private toggle(): void {
    // Un refus du navigateur (geste non reconnu, politique de la page) laisse simplement la carte en place.
    const request = this.isFullscreen ? document.exitFullscreen() : this.target.requestFullscreen();
    request.catch(() => {});
  }

  private render(): void {
    const label = this.isFullscreen ? EXIT_LABEL : ENTER_LABEL;
    this.el.setAttribute("aria-label", label);
    this.el.title = label;
    this.el.replaceChildren(createFullscreenIcon(this.isFullscreen));
  }

  /** Voir `GeolocationButton.setStackOffset`. */
  setStackOffset(offsetPx: number): void {
    this.el.style[this.edgeProperty] = `${offsetPx}px`;
  }

  destroy(): void {
    document.removeEventListener("fullscreenchange", this.onFullscreenChange);
    this.el.remove();
  }
}

/** Deux flèches en diagonale, vers les coins pour entrer, vers le centre pour sortir : les quatre coins sont déjà ceux de `ResetViewButton`. */
function createFullscreenIcon(exit: boolean): SVGSVGElement {
  const SVG_NS = "http://www.w3.org/2000/svg";
  const svg = document.createElementNS(SVG_NS, "svg") as SVGSVGElement;
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("width", "18");
  svg.setAttribute("height", "18");
  svg.setAttribute("fill", "none");
  svg.setAttribute("stroke", "currentColor");
  svg.setAttribute("stroke-width", "2");
  svg.setAttribute("stroke-linecap", "round");
  svg.setAttribute("stroke-linejoin", "round");
  const arrows = exit
    ? ["M4 14h6v6", "M20 10h-6V4", "M14 10l7-7", "M3 21l7-7"]
    : ["M15 3h6v6", "M9 21H3v-6", "M21 3l-7 7", "M3 21l7-7"];
  for (const d of arrows) {
    const corner = document.createElementNS(SVG_NS, "path");
    corner.setAttribute("d", d);
    svg.appendChild(corner);
  }
  return svg;
}
